import { unwrapResult } from '@reduxjs/toolkit';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, FieldArray, Form } from 'formik';
import { Box, Text, Button, Modal } from '@rsos/components/capstone/base';
import Loader from '@rsos/components/capstone/base/Button/Loader';
import {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import {
  addAgency,
  removeDeclinedAgencyLinksTo,
  setPendingAgencyLinksTo,
} from '@rsos/reduxDataStore/src/features/alertsRouting/alertsRoutingSlice';
import { notifyError } from '@rsos/utils/toastUtils';
import { alertTypeFormatter } from '../../Common/utils';
import { schema } from '../utils';
import AlertTypesEmptyState from './AlertTypesEmptyState';
import AuthorityNameSelect from './AuthorityNameSelect';
import IsFetchError from './IsFetchError';
import NoAgenciesAvailable from './NoAgenciesAvailable';
import {
  ScrollableContainer,
  Checkbox,
  SectionTitle,
  RowText,
  IsFetchingContainer,
} from './PairAuthorityModal.styles';

const PairAgencyModal = ({
  isOpened,
  onClose,
  alertTypes,
  isFetching,
  isFetchError,
  authorities,
  retryFetchAvailableAgencies,
}) => {
  const { sinatra } = useSelector(state => state);
  const org = sinatra.user.currentOrg.name;
  const [selectedAuthority, setSelectedAuthority] = useState({});
  const dispatch = useDispatch();

  const handleCheckbox = (e, arrayHelpers, type, values) => {
    if (e.target.checked) arrayHelpers.push(type);
    else {
      const idx = values.alertTypes.indexOf(type);
      arrayHelpers.remove(idx);
    }
  };
  const handleClose = handleReset => {
    handleReset();
    onClose();
  };
  const handleAddAgencyPair = (values, resetForm) => {
    let data = {
      org,
      link_to: selectedAuthority?.id,
      alert_emergency_types: values.alertTypes,
    };
    dispatch(addAgency(data))
      .then(unwrapResult)
      .then(response => {
        dispatch(removeDeclinedAgencyLinksTo(response.link_to.id));
        dispatch(setPendingAgencyLinksTo(response));
        resetForm();
        onClose();
      })
      .catch(error => {
        notifyError(error);
      });
  };

  const handleAgencyNameChange = (selectedOption, cb) => {
    if (selectedOption) {
      cb('authorityName', selectedOption);
      const authority = authorities.find(a => a.id === selectedOption.id);
      setSelectedAuthority(authority);
    } else {
      cb('authorityName', '');
      setSelectedAuthority({});
    }
    cb('alertTypes', []);
  };

  return (
    <Formik
      initialValues={{ alertTypes: [], authorityName: '' }}
      enableReinitialize
      validationSchema={schema}
      onSubmit={(values, { resetForm }) =>
        handleAddAgencyPair(values, resetForm)
      }
    >
      {({ values, handleReset, setFieldValue }) => (
        <Modal
          isOpened={isOpened}
          onClose={() => handleClose(handleReset)}
          width={400}
          id="modal-pair-agency"
          disableBackdropClick
        >
          <>
            <ModalHeader onClose={() => handleClose(handleReset)}>
              <Box width={400}>
                <Text size="xlarge" color="primaryText">
                  Pair an agency
                </Text>
              </Box>
            </ModalHeader>
            <ModalBody>
              <Form id="pairAgencyForm">
                {isFetchError ? (
                  <IsFetchError
                    error={isFetchError}
                    retryFetchAvailableAgencies={retryFetchAvailableAgencies}
                  />
                ) : isFetching ? (
                  <IsFetchingContainer>
                    <Loader size={14} color="secondaryText" />{' '}
                    <Text size="normal" color="secondaryText">
                      Fetching available agencies
                    </Text>
                  </IsFetchingContainer>
                ) : authorities.length === 0 ? (
                  <NoAgenciesAvailable />
                ) : (
                  <AuthorityNameSelect
                    value={values.authorityName}
                    onChange={selectedOption =>
                      handleAgencyNameChange(selectedOption, setFieldValue)
                    }
                    label="Agency Name"
                    data={authorities}
                    isDisabled={!authorities?.length}
                  />
                )}

                <ScrollableContainer>
                  <FieldArray name="alertTypes">
                    {arrayHelpers =>
                      alertTypes.length ? (
                        <div
                          id="alert-types-checkbox-group"
                          aria-labelledby="alert-types-checkbox-group"
                          role="group"
                        >
                          <SectionTitle>Alert type</SectionTitle>
                          {alertTypes.map(type => (
                            <Box horizontal align="center" py={13} key={type}>
                              <Checkbox
                                type="checkbox"
                                data-name={type}
                                name={type}
                                value={type}
                                disabled={!values.authorityName}
                                checked={
                                  !!values.alertTypes.find(p => p === type)
                                }
                                onChange={e =>
                                  handleCheckbox(e, arrayHelpers, type, values)
                                }
                              />
                              <RowText disabled={!values.authorityName}>
                                {alertTypeFormatter(type)}
                              </RowText>
                            </Box>
                          ))}
                        </div>
                      ) : (
                        <AlertTypesEmptyState />
                      )
                    }
                  </FieldArray>
                </ScrollableContainer>
              </Form>
            </ModalBody>
            <ModalFooter flow={12}>
              <Button btnType="tertiary" onClick={onClose} grow noMargin>
                Cancel
              </Button>
              <Button
                onClick={() => {}}
                grow
                noMargin
                type="submit"
                form="pairAgencyForm"
                disabled={!values?.alertTypes.length}
              >
                Request Pair
              </Button>
            </ModalFooter>
          </>
        </Modal>
      )}
    </Formik>
  );
};

export default PairAgencyModal;
